import { ArrowRightIcon, DollarSignIcon } from 'lucide-react'
import React, { useEffect } from 'react'
import { useForm } from 'react-hook-form'

import { type GrapeAPIErrorType } from 'components/hooks/GrapeAPIError'
import ProofOfCoverageFileUpload from 'components/member_onboarding_v2/components/ProofOfCoverageFileUpload'
import {
  FormCheckboxFieldDS,
  FormFormattedNumberFieldDS,
  FormRadioGroupDS,
  FormTextFieldDS,
} from 'components/reusable/form/FormFields'
import { Banner, Button, ButtonGroup, useBreakpoint } from 'slab'
import { type GrapeMoney } from 'types'
import {
  convertNillableGrapeMoneyToNumber,
  getFormattedDate,
  getNumericValueFromNumberOrString,
} from 'utils'
import {
  useGetMemberOnboardingProofOfCoverageMostRecent as useGetProofOfCoverage,
  usePostMemberOnboardingEnrollmentEventsEnrollmentEventIdOffThatchEnrollmentsEnroll as useCreateOffThatchEnrollment,
} from 'utils/web_api'

import { OnboardingGenericErrorMessage } from '../components/OnboardingGenericErrorMessage'
import { emitByopPlanDetailsFormPageViewEvent } from '../events'
import { useOnboarding } from '../OnboardingContext'
import { type StepComponentProps } from '../types'
import { isRenewalOrQleFlow } from '../utils'

type BasicInfoProps = {
  carrier_name: string;
  plan_code: string;
  quoted_premium: string | number;
  off_exchange: string | undefined;
  attestation: boolean;
  payment_method: string;
}

type OffThatchMedicalPlanDetailsProps = StepComponentProps & {
  setViewingOffThatch?: (viewingOffThatch: boolean) => void;
  setShowPlanDetails: (showPlanDetails: boolean) => void;
}

export function OffThatchMedicalPlanDetails (
  props: OffThatchMedicalPlanDetailsProps
) {
  const {
    navigateToNextStep,
    navigateToSpecificStep,
    setViewingOffThatch,
    setShowPlanDetails,
    userId,
  } = props

  const { onboardingData, enrollmentEventId } = useOnboarding()

  useEffect(() => {
    if (onboardingData && userId) {
      emitByopPlanDetailsFormPageViewEvent(userId, onboardingData)
    }
  }, [onboardingData, userId])

  const [submitError, setSubmitError] =
    React.useState<GrapeAPIErrorType | null>(null)

  const { breakpointIsAtLeast } = useBreakpoint()

  const createEnrollment = useCreateOffThatchEnrollment({
    mutation: {
      onSuccess: () => {
        navigateToNextStep?.()
      },
      onError: (error) => {
        setSubmitError(error as GrapeAPIErrorType)
      },
    },
  })
  const soonestCoverageStartDate =
    onboardingData?.member?.soonest_coverage_start_date
  const existingEnrollment = onboardingData?.enrollments?.find(
    (enrollment) =>
      enrollment.covers_medical &&
      enrollment.payment_method !== 'paid_by_thatch'
  )

  const planYear = onboardingData?.enrollment_event?.plan_year
  const { data: proofOfCoverage } = useGetProofOfCoverage({
    plan_year: planYear,
  })

  const existingQuotedPremium =
    existingEnrollment?.quoted_premium as GrapeMoney

  const getDefaultValues = () => ({
    carrier_name: existingEnrollment?.carrier_name || '',
    plan_code: existingEnrollment?.plan_code || '',
    off_exchange: existingEnrollment?.off_exchange?.toString(),
    quoted_premium: existingQuotedPremium == null
      ? ''
      : convertNillableGrapeMoneyToNumber(existingQuotedPremium),
    attestation: !!existingEnrollment, // if there's an existing enrollment, assume already attested
    payment_method: existingEnrollment?.payment_method || 'thatch_card',
  })

  const onSubmit = (formData: BasicInfoProps) => {
    const premiumNumber = getNumericValueFromNumberOrString(
      formData.quoted_premium
    )

    createEnrollment.mutate(
      {
        enrollmentEventId,
        data: {
          plan_code: formData.plan_code,
          carrier_name: formData.carrier_name,
          quoted_premium: premiumNumber,
          off_exchange: formData.off_exchange === 'true',
          payment_source: formData.payment_method,
          attestation: formData.attestation,
        },
      },
      {
        onSuccess: () => {
          navigateToNextStep?.()
        },
      }
    )
  }

  const {
    control,
    handleSubmit,
    register,
    watch,
    formState: { isValid, errors },
  } = useForm<BasicInfoProps>({
    defaultValues: getDefaultValues(),
    mode: 'onChange',
  })

  const offExchangeValue = watch('off_exchange')

  if (submitError) {
    return (
      <Banner
        title='A problem has occurred while processing your request'
        type='critical'
      >
        <OnboardingGenericErrorMessage error={submitError} />
      </Banner>
    )
  }

  const attestationText = soonestCoverageStartDate
    ? `I attest that I have (or am planning to get) the medical coverage
   that's listed above, it's eligible for my employer allowance,
   and it'll be active on ${getFormattedDate(soonestCoverageStartDate, {
     month: 'long',
     timeZone: 'UTC',
   })}`
    : "I attest that I have (or am planning to get) the medical coverage and it's eligible for my employer allowance"

  function renderButtons () {
    const formIsValid =
      proofOfCoverage && proofOfCoverage.file_id.length > 0 && isValid

    const backButton = (
      <Button
        size='large'
        variant='secondary'
        onClick={() => {
          if (onboardingData && isRenewalOrQleFlow(onboardingData)) {
            navigateToSpecificStep?.('renewal_show_summary')
          } else {
            setShowPlanDetails(false)
          }
        }}
      >
        Back
      </Button>
    )

    const onThatchButton = !!setViewingOffThatch && (
      <Button
        variant='tertiary-deemphasized'
        onClick={() => setViewingOffThatch(false)}
      >
        Choose a new plan on Thatch instead
      </Button>
    )

    const nextButton = (
      <Button
        variant='primary'
        type='submit'
        disabled={createEnrollment.isPending || !formIsValid}
        rightIcon={ArrowRightIcon}
      >
        Next
      </Button>
    )

    if (breakpointIsAtLeast('xs')) {
      return (
        <div className='flex items-center justify-between'>
          {backButton}
          <ButtonGroup size='large'>
            {onThatchButton}
            {nextButton}
          </ButtonGroup>
        </div>
      )
    }

    return (
      <ButtonGroup size='large' layout='vertical'>
        {nextButton}
        {backButton}
        {onThatchButton}
      </ButtonGroup>
    )
  }

  return (
    <div
      data-testid='OffThatchMedicalDisplay-planDetails'
      className='max-w-[44rem]'
    >
      <h1 className='heading-lg text-gray-800 mb-5'>Use my own plan</h1>
      <h2 className='heading-sm text-gray-800 mt-6 mb-5'>
        Tell us about your plan
      </h2>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className='mb-6'>
          <FormTextFieldDS<BasicInfoProps>
            disabled={createEnrollment.isPending}
            fieldId='carrier_name'
            fieldLabel='Insurance company'
            register={register}
            placeholder='e.g. Anthem, Kaiser, United Healthcare'
            validationRule={{ required: 'Insurance company is required.' }}
          />
        </div>
        <div className='mb-6'>
          <FormTextFieldDS<BasicInfoProps>
            disabled={createEnrollment.isPending}
            fieldId='plan_code'
            fieldLabel='Plan name'
            register={register}
            placeholder='Silver 80 Value HMO'
            validationRule={{ required: 'Plan name is required.' }}
          />
        </div>
        <div className='mb-6'>
          <FormFormattedNumberFieldDS<BasicInfoProps>
            error={errors.quoted_premium}
            fieldId='quoted_premium'
            fieldLabel='Monthly premium'
            register={register}
            placeholder='0.00'
            leadingIcon={DollarSignIcon}
            validationRule={{
              required: 'Monthly premium is required.',
              validate: (value: string | number) => {
                const numericValue = getNumericValueFromNumberOrString(value)
                if (numericValue >= 1000000) {
                  return "This premium doesn't seem right, are you missing a decimal point?"
                }
                return true
              },
            }}
          />
        </div>
        <div className='mb-6'>
          <FormRadioGroupDS
            control={control}
            name='off_exchange'
            label='I signed up for it:'
            rules={{ required: 'Please select how you signed up for your plan.' }}
            options={[
              {
                label:
                  'on a marketplace exchange (such as healthcare.gov or coveredca.com)',
                value: 'false',
              },
              {
                label:
                  'directly from an insurance company (like anthem.com, cigna.com, etc.)',
                value: 'true',
              },
            ]}
          />
          {offExchangeValue === 'false' && (
            <div className='mt-4'>
              <Banner
                title="You'll be paying for this plan post-tax, so your allowance might not go as far."
                type='notice'
              />
            </div>
          )}
        </div>
        <div className='mb-4'>
          <legend className='body-sm-bold text-gray-800 mb-2'>
            Upload a PDF or screenshot of your plan information:
          </legend>
          <ProofOfCoverageFileUpload
            proofOfCoverage={proofOfCoverage}
            planYear={planYear?.toString() ?? ''}
          />
        </div>
        <legend className='body-sm-bold text-gray-800 mb-2'>Confirm:</legend>
        <FormCheckboxFieldDS
          fieldId='attestation'
          fieldLabel={attestationText}
          register={register}
          validationRule={{ required: 'Your attestation is required.' }}
        />
        <div className='my-10'>
          <h2 className='heading-sm text-gray-800 mt-6 mb-5'>
            How will you be paying?
          </h2>
          <FormRadioGroupDS
            control={control}
            name='payment_method'
            label=''
            options={[
              {
                label: (
                  <p>
                    <span className='font-semibold'>
                      Give me a Thatch card:
                    </span>{' '}
                    I'll use it to set my insurance up on autopay
                  </p>
                ),
                value: 'thatch_card',
              },
              {
                label: (
                  <p>
                    <span className='font-semibold'>
                      Give me a Thatch bank account (ACH):
                    </span>{' '}
                    My insurance doesn't accept credit card payments, but I'd
                    still like to set up autopay
                  </p>
                ),
                value: 'thatch_bank_account',
              },
              {
                label: (
                  <p>
                    <span className='font-semibold'>Myself:</span> I'll pay for
                    my health insurance out of pocket, then submit receipts to
                    be reimbursed by Thatch
                  </p>
                ),
                value: 'reimbursement',
              },
            ]}
          />
        </div>
        {renderButtons()}
      </form>
    </div>
  )
}
